import { create, fromBinary, toBinary } from '@bufbuild/protobuf';
import {
  ArchiveClientV1Schema,
  BeginDocumentDownloadV1Schema,
  CommitDocumentUploadV1Schema,
  CommandRequestV1Schema,
  CreateClientV1Schema,
  FileChunkV1Schema,
  FileTransferV1Schema,
  GetChangesAfterV1Schema,
  EntityTypeV1,
  EnvelopeV1Schema,
  ErrorCodeV1,
  ListClientsRequestV1Schema,
  ListDocumentsRequestV1Schema,
  PrepareDocumentUploadV1Schema,
  PresenceStateV1,
  PresenceV1Schema,
  QueryRequestV1Schema,
  SessionConfirmV1Schema,
  SessionControlV1Schema,
  SyncStartV1,
  UpdateClientV1Schema,
  validateEnvelope,
} from '@mlo-local/protocol';
import type {
  ClientSnapshotV1,
  EnvelopeV1,
  FileTransferV1,
  UploadPreparedV1,
  PresenceV1,
  ResponseV1,
  SessionReadyV1,
} from '@mlo-local/protocol';
import {
  decodeFrame,
  encodeFrame,
  FRAME_CHANNEL,
  FRAME_DIRECTION,
  ReliableSequenceValidator,
  PresenceReplayWindow,
} from '@mlo-local/protocol/frame.js';
import { sha256 } from '@mlo-local/protocol/crypto.js';
import type { SessionChannelMaterial, WebCryptoKey } from '@mlo-local/protocol/crypto.js';
import type {
  AppTransport,
  ClientCreateInput,
  ClientListRequest,
  ClientUpdateInput,
  ClientView,
  DocumentView,
  TransportStatus,
} from './foundation.js';
import { randomUuid } from './uuid.js';

type DataChannelState = 'connecting' | 'open' | 'closing' | 'closed';

export interface RemoteHandshakeMaterial {
  readonly browserPublicKeyFingerprint: Uint8Array;
  readonly phonePublicKeyFingerprint: Uint8Array;
  readonly offerHash: Uint8Array;
  readonly protocolMajor?: number;
}

export type RemoteSessionSource = {
  readonly sessionId: Uint8Array;
  /** Legacy control-only source; new callers should provide all physical lanes. */
  readonly dataChannel?: EphemeralDataChannel | null;
  readonly channels?: Partial<Record<'control' | 'file' | 'presence', EphemeralDataChannel | null>>;
  readonly channelMaterials: {
    readonly browserToPhone: Record<'control' | 'file' | 'presence', SessionChannelMaterial>;
    readonly phoneToBrowser: Record<'control' | 'file' | 'presence', SessionChannelMaterial>;
  };
  readonly handshakeMaterial: RemoteHandshakeMaterial;
};

/** Structural bridge used by the customer shell; connector owns the WebRTC implementation. */
export type BrowserSessionSource = {
  connect(): Promise<void>;
  waitForDataChannel(
    lane: 'control' | 'file' | 'presence',
    timeoutMs?: number,
  ): Promise<EphemeralDataChannel>;
  readonly channelMaterials: RemoteSessionSource['channelMaterials'];
  readonly handshakeMaterial: RemoteHandshakeMaterial;
  readonly sessionId: Uint8Array;
};

/** Waits for all three physical lanes before exposing the application transport. */
export async function createRemoteAppTransportFromSession(
  session: BrowserSessionSource,
  requestId?: () => string,
): Promise<RemoteAppTransport> {
  await session.connect();
  const [control, file, presence] = await Promise.all([
    session.waitForDataChannel('control', DATA_CHANNEL_NEGOTIATION_TIMEOUT_MS),
    session.waitForDataChannel('file', DATA_CHANNEL_NEGOTIATION_TIMEOUT_MS),
    session.waitForDataChannel('presence', DATA_CHANNEL_NEGOTIATION_TIMEOUT_MS),
  ]);
  return createRemoteAppTransport(
    {
      sessionId: session.sessionId,
      channels: { control, file, presence },
      channelMaterials: session.channelMaterials,
      handshakeMaterial: session.handshakeMaterial,
    },
    requestId,
  );
}

/**
 * Five concurrent phone-hosted peers can negotiate sequentially on a mobile WebRTC worker.
 * Keep the wait bounded, but allow the documented multi-browser capacity to finish ICE and
 * data-channel setup before failing closed.
 */
export const DATA_CHANNEL_NEGOTIATION_TIMEOUT_MS = 90_000;

/** Composes the connector's ephemeral browser session with the application RPC adapter. */
export function createRemoteAppTransport(
  source: RemoteSessionSource,
  requestId: () => string = randomUuid,
): RemoteAppTransport {
  const channel = source.dataChannel;
  const channels = source.channels ?? {};
  const controlChannel = channels.control ?? channel;
  if (!controlChannel) throw new Error('DATA_CHANNEL_UNAVAILABLE');
  const inbound = source.channelMaterials.browserToPhone.control;
  const outbound = source.channelMaterials.phoneToBrowser.control;
  return new RemoteAppTransport(
    controlChannel,
    inbound.key,
    inbound.noncePrefix,
    source.sessionId,
    // The phone assigns the actor during SessionReady; this zero placeholder is never sent
    // because the handshake replaces it before application commands are allowed.
    new Uint8Array(16),
    requestId,
    outbound.key,
    outbound.noncePrefix,
    source.handshakeMaterial,
    channels.file ?? undefined,
    channels.presence ?? undefined,
    channels.file ? source.channelMaterials.browserToPhone.file.key : undefined,
    channels.file ? source.channelMaterials.browserToPhone.file.noncePrefix : undefined,
    channels.file ? source.channelMaterials.phoneToBrowser.file.key : undefined,
    channels.file ? source.channelMaterials.phoneToBrowser.file.noncePrefix : undefined,
    channels.presence ? source.channelMaterials.phoneToBrowser.presence.key : undefined,
    channels.presence ? source.channelMaterials.phoneToBrowser.presence.noncePrefix : undefined,
    channels.presence ? source.channelMaterials.browserToPhone.presence.key : undefined,
    channels.presence ? source.channelMaterials.browserToPhone.presence.noncePrefix : undefined,
  );
}

export interface EphemeralDataChannel {
  readonly readyState: DataChannelState;
  /** Native RTCDataChannel defaults to Blob in some browsers; force arraybuffer when available. */
  binaryType?: 'blob' | 'arraybuffer';
  send(data: ArrayBuffer | Uint8Array): void;
  addEventListener(
    type: 'open' | 'message' | 'close' | 'error',
    listener: (event: unknown) => void,
  ): void;
  removeEventListener(
    type: 'open' | 'message' | 'close' | 'error',
    listener: (event: unknown) => void,
  ): void;
  close(): void;
}

export class RemoteConflictError extends Error {
  constructor(
    public readonly current: ClientView,
    public readonly rejectedOperationId: string,
  ) {
    super('CONFLICT');
    this.name = 'RemoteConflictError';
  }
}

/**
 * Browser-side RPC adapter for the phone's encrypted WebRTC data channel.
 * Payloads are canonical protobuf envelopes from T-002; browser state remains
 * process-memory only and closing the channel destroys all pending requests.
 */
export class RemoteAppTransport implements AppTransport {
  #status: TransportStatus = 'DISCONNECTED';
  #sequence = 1n;
  #listeners = new Set<(clients: readonly ClientView[]) => void>();
  #pending = new Map<
    string,
    { resolve: (value: unknown) => void; reject: (error: Error) => void }
  >();
  #incoming = new ReliableSequenceValidator();
  #fileSequence = 1n;
  #incomingFile = new ReliableSequenceValidator();
  #presenceSequence = 1n;
  #presenceCounter = 1n;
  #incomingPresence = new PresenceReplayWindow();
  #fileListeners = new Set<(transfer: FileTransferV1) => void>();
  #presenceListeners = new Set<(presence: PresenceV1) => void>();
  #clients = new Map<string, ClientView>();
  #clientListRequest: ClientListRequest = {};
  #documents = new Map<string, Map<string, DocumentView>>();
  #documentListeners = new Map<string, Set<(documents: readonly DocumentView[]) => void>>();
  #openPromise: Promise<void> | undefined;
  #handshakePromise: Promise<SessionReadyV1> | undefined;
  #sessionReady = false;
  #syncCursor: Uint8Array | undefined;
  private actorProfileId: Uint8Array;
  private readonly requestId: () => string;
  readonly #onOpen = () => {
    this.#status = this.handshake ? 'CONNECTING' : 'CONNECTED';
  };
  readonly #onClose = () => {
    this.failClosed(new Error('SESSION_DISCONNECTED'));
  };
  readonly #onError = () => {
    this.failClosed(new Error('SESSION_CHANNEL_ERROR'));
  };
  readonly #onMessage = (event: unknown) => {
    void this.handleMessage(event);
  };
  readonly #onFileMessage = (event: unknown) => {
    void this.handleFileMessage(event);
  };
  readonly #onPresenceMessage = (event: unknown) => {
    void this.handlePresenceMessage(event);
  };

  constructor(
    private readonly channel: EphemeralDataChannel,
    private readonly key: WebCryptoKey,
    private readonly noncePrefix: Uint8Array,
    private readonly sessionId: Uint8Array,
    actorProfileIdOrRequestId: Uint8Array | (() => string) = new Uint8Array(16),
    requestId: () => string = randomUuid,
    private readonly responseKey: WebCryptoKey = key,
    private readonly responseNoncePrefix: Uint8Array = noncePrefix,
    private readonly handshake?: RemoteHandshakeMaterial,
    private readonly fileChannel?: EphemeralDataChannel,
    private readonly presenceChannel?: EphemeralDataChannel,
    private readonly fileKey?: WebCryptoKey,
    private readonly fileNoncePrefix?: Uint8Array,
    private readonly fileResponseKey?: WebCryptoKey,
    private readonly fileResponseNoncePrefix?: Uint8Array,
    private readonly presenceResponseKey?: WebCryptoKey,
    private readonly presenceResponseNoncePrefix?: Uint8Array,
    private readonly presenceKey?: WebCryptoKey,
    private readonly presenceNoncePrefix?: Uint8Array,
  ) {
    this.actorProfileId =
      typeof actorProfileIdOrRequestId === 'function'
        ? new Uint8Array(16)
        : actorProfileIdOrRequestId;
    this.requestId =
      typeof actorProfileIdOrRequestId === 'function' ? actorProfileIdOrRequestId : requestId;
    if (
      noncePrefix.byteLength !== 4 ||
      responseNoncePrefix.byteLength !== 4 ||
      sessionId.byteLength !== 16 ||
      this.actorProfileId.byteLength !== 16
    )
      throw new Error('INVALID_CHANNEL_MATERIAL');
    if (
      handshake &&
      (handshake.browserPublicKeyFingerprint.byteLength !== 32 ||
        handshake.phonePublicKeyFingerprint.byteLength !== 32 ||
        handshake.offerHash.byteLength !== 32 ||
        (handshake.protocolMajor ?? 1) !== 1)
    )
      throw new Error('INVALID_HANDSHAKE_MATERIAL');
    if ('binaryType' in channel) channel.binaryType = 'arraybuffer';
    channel.addEventListener('open', this.#onOpen);
    channel.addEventListener('close', this.#onClose);
    channel.addEventListener('error', this.#onError);
    channel.addEventListener('message', this.#onMessage);
    this.fileChannel?.addEventListener('close', this.#onClose);
    this.fileChannel?.addEventListener('error', this.#onError);
    this.fileChannel?.addEventListener('message', this.#onFileMessage);
    this.presenceChannel?.addEventListener('close', this.#onClose);
    this.presenceChannel?.addEventListener('error', this.#onError);
    this.presenceChannel?.addEventListener('message', this.#onPresenceMessage);
  }

  get status(): TransportStatus {
    return this.#status;
  }

  get dataChannels(): {
    readonly control: EphemeralDataChannel;
    readonly file?: EphemeralDataChannel;
    readonly presence?: EphemeralDataChannel;
  } {
    return {
      control: this.channel,
      ...(this.fileChannel ? { file: this.fileChannel } : {}),
      ...(this.presenceChannel ? { presence: this.presenceChannel } : {}),
    };
  }

  get syncCursor(): Uint8Array | undefined {
    return this.#syncCursor ? new Uint8Array(this.#syncCursor) : undefined;
  }

  async connect(): Promise<void> {
    if (this.#status === 'CONNECTED' && (!this.handshake || this.#sessionReady)) return;
    this.#status = 'CONNECTING';
    if (this.channel.readyState === 'open') {
      if (this.handshake && !this.#sessionReady) await this.performHandshake();
      this.#status = 'CONNECTED';
      return;
    }
    if (!this.#openPromise) {
      this.#openPromise = new Promise<void>((resolve, reject) => {
        const open = () => {
          cleanup();
          resolve();
        };
        const fail = () => {
          cleanup();
          this.#status = 'DISCONNECTED';
          reject(new Error('SESSION_NOT_CONNECTED'));
        };
        const cleanup = () => {
          this.channel.removeEventListener('open', open);
          this.channel.removeEventListener('close', fail);
          this.channel.removeEventListener('error', fail);
        };
        this.channel.addEventListener('open', open);
        this.channel.addEventListener('close', fail);
        this.channel.addEventListener('error', fail);
      }).finally(() => {
        this.#openPromise = undefined;
      });
    }
    await this.#openPromise;
    if (this.handshake && !this.#sessionReady) await this.performHandshake();
    this.#status = 'CONNECTED';
  }

  async listClients(request: ClientListRequest = {}): Promise<readonly ClientView[]> {
    this.#clientListRequest = { ...request };
    const query = create(QueryRequestV1Schema, {
      query: {
        case: 'listClients',
        value: create(ListClientsRequestV1Schema, {
          query: request.query ?? '',
          status: request.status ?? '',
          includeArchived: request.includeArchived ?? false,
          // The Android repository caps keyset pages at 100; pagination will be added to the
          // view model once the cursor is exposed through AppTransport.
          pageSize: 100,
        }),
      },
    });
    const response = await this.call({ case: 'query', value: query });
    if (response.outcome.case !== 'clientPage') throw new Error('MALFORMED_RESPONSE');
    const clients = response.outcome.value.clients.map(clientFromProto);
    this.#clients = new Map(clients.map((client) => [client.id, client]));
    this.emitClients();
    return clients;
  }

  async listDocuments(clientId: string): Promise<readonly DocumentView[]> {
    const query = create(QueryRequestV1Schema, {
      query: {
        case: 'listDocuments',
        value: create(ListDocumentsRequestV1Schema, {
          clientId: uuidToBytes(clientId),
          pageSize: 100,
        }),
      },
    });
    const response = await this.call({ case: 'query', value: query });
    if (response.outcome.case !== 'documentPage') throw new Error('MALFORMED_DOCUMENT_RESPONSE');
    const documents = response.outcome.value.documents.map(documentFromProto);
    this.#documents.set(clientId, new Map(documents.map((document) => [document.id, document])));
    this.emitDocuments(clientId);
    return documents;
  }

  async prepareDocumentUpload(input: {
    clientId: string;
    file: File;
    displayName?: string;
    category?: string;
    notes?: string;
  }): Promise<{ operationId: string; prepared: UploadPreparedV1 }> {
    const operationId = this.requestId();
    const query = create(QueryRequestV1Schema, {
      query: {
        case: 'prepareDocumentUpload',
        value: create(PrepareDocumentUploadV1Schema, {
          target: { case: 'clientId', value: uuidToBytes(input.clientId) },
          operationId: uuidToBytes(operationId),
          displayName: input.displayName ?? input.file.name,
          category: input.category ?? 'GENERAL',
          notes: input.notes ?? '',
          originalFileName: input.file.name,
          declaredMime: input.file.type || 'application/octet-stream',
          declaredSize: BigInt(input.file.size),
        }),
      },
    });
    const response = await this.call({ case: 'query', value: query });
    if (response.outcome.case !== 'uploadPrepared') throw new Error('MALFORMED_UPLOAD_RESPONSE');
    return { operationId, prepared: response.outcome.value };
  }

  async uploadDocument(
    operationId: string,
    prepared: UploadPreparedV1,
    file: File,
  ): Promise<DocumentView> {
    if (prepared.chunkSize < 1 || prepared.chunkSize > 16 * 1024) {
      throw new Error('INVALID_UPLOAD_CHUNK_SIZE');
    }
    const plaintextSha256 = await sha256(new Uint8Array(await file.arrayBuffer()));
    let index = 0;
    for (let offset = 0; offset < file.size; offset += prepared.chunkSize) {
      const bytes = new Uint8Array(
        await file.slice(offset, offset + prepared.chunkSize).arrayBuffer(),
      );
      const chunk = create(FileChunkV1Schema, {
        transferId: prepared.transferId,
        index,
        bytes,
        chunkSha256: await sha256(bytes),
      });
      await this.sendFileTransfer(
        create(FileTransferV1Schema, {
          message: { case: 'uploadChunk', value: chunk },
        }),
      );
      index += 1;
    }
    await this.connect();
    const command = create(CommandRequestV1Schema, {
      operationId: uuidToBytes(operationId),
      actorProfileId: this.actorProfileId,
      entityType: EntityTypeV1.DOCUMENT,
      entityId: prepared.documentId,
      baseRevision: 0n,
      command: {
        case: 'commitDocumentUpload',
        value: create(CommitDocumentUploadV1Schema, {
          transferId: prepared.transferId,
          documentId: prepared.documentId,
          fileVersionId: prepared.fileVersionId,
          totalChunks: index,
          plaintextSha256,
        }),
      },
    });
    const response = await this.call({ case: 'request', value: command });
    if (response.outcome.case !== 'document') throw new Error('MALFORMED_DOCUMENT_RESPONSE');
    const document = documentFromProto(response.outcome.value);
    this.upsertDocument(document);
    return document;
  }

  async downloadDocument(
    fileVersionId: string,
  ): Promise<{ file: Blob; name: string; mimeType: string }> {
    const query = create(QueryRequestV1Schema, {
      query: {
        case: 'beginDocumentDownload',
        value: create(BeginDocumentDownloadV1Schema, { fileVersionId: uuidToBytes(fileVersionId) }),
      },
    });
    const response = await this.call({ case: 'query', value: query });
    if (response.outcome.case !== 'downloadPrepared')
      throw new Error('MALFORMED_DOWNLOAD_RESPONSE');
    const prepared = response.outcome.value;
    const chunks = new Map<number, Uint8Array>();
    let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
    const completed = new Promise<void>((resolve, reject) => {
      const unsubscribe = this.subscribeFile((transfer) => {
        if (transfer.message.case === 'downloadChunk') {
          const chunk = transfer.message.value;
          if (equalBytes(chunk.transferId, prepared.transferId)) {
            chunks.set(chunk.index, new Uint8Array(chunk.bytes));
          }
        } else if (
          transfer.message.case === 'downloadComplete' &&
          equalBytes(transfer.message.value.transferId, prepared.transferId)
        ) {
          unsubscribe();
          if (timer !== undefined) globalThis.clearTimeout(timer);
          resolve();
        }
      });
      timer = globalThis.setTimeout(
        () => {
          unsubscribe();
          reject(new Error('DOWNLOAD_TIMEOUT'));
        },
        Math.max(1_000, Number(prepared.expiresAtMs) - Date.now()),
      );
    });
    await completed;
    const ordered = [...chunks.entries()].sort(([left], [right]) => left - right);
    if (ordered.length !== prepared.totalChunks) throw new Error('DOWNLOAD_CHUNK_COUNT');
    const bytes = new Uint8Array(ordered.reduce((total, [, chunk]) => total + chunk.byteLength, 0));
    let offset = 0;
    ordered.forEach(([, chunk]) => {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    });
    if (!equalBytes(await sha256(bytes), prepared.plaintextSha256))
      throw new Error('DOWNLOAD_DIGEST');
    return {
      file: new Blob([bytes], { type: prepared.mimeType }),
      name: prepared.originalFileName,
      mimeType: prepared.mimeType,
    };
  }

  /** Replays the phone's opaque, scope-bound cursor after a reconnect or missed broadcast. */
  async replayChanges(cursor?: Uint8Array): Promise<readonly ClientView[]> {
    const query = create(QueryRequestV1Schema, {
      query: {
        case: 'getChangesAfter',
        value: create(GetChangesAfterV1Schema, {
          pageSize: 100,
          ...((cursor ?? this.#syncCursor)
            ? { cursor: new Uint8Array(cursor ?? this.#syncCursor!) }
            : {}),
        }),
      },
    });
    const response = await this.call({ case: 'query', value: query });
    if (response.outcome.case !== 'changePage') throw new Error('MALFORMED_CHANGE_PAGE');
    const page = response.outcome.value;
    this.#syncCursor =
      page.nextCursor.byteLength > 0 ? new Uint8Array(page.nextCursor) : this.#syncCursor;
    const clients = page.events.flatMap((event) =>
      event.representation.case === 'client' && event.representation.value
        ? [clientFromProto(event.representation.value)]
        : [],
    );
    clients.forEach((client) => this.#clients.set(client.id, client));
    if (clients.length > 0) this.emitClients();
    return clients;
  }

  async createClient(input: ClientCreateInput): Promise<ClientView> {
    await this.connect();
    const operationId = uuidToBytes(randomUuid());
    const command = create(CommandRequestV1Schema, {
      operationId,
      actorProfileId: this.actorProfileId,
      entityType: EntityTypeV1.CLIENT,
      baseRevision: 0n,
      command: {
        case: 'createClient',
        value: create(CreateClientV1Schema, {
          ownerActorId: input.ownerActorId ? uuidToBytes(input.ownerActorId) : this.actorProfileId,
          name: input.name,
          email: input.email,
          ...(input.customFields ? { customFields: input.customFields } : {}),
        }),
      },
    });
    const response = await this.call({ case: 'request', value: command });
    if (response.outcome.case === 'conflict') {
      if (response.outcome.value.representation.case !== 'client')
        throw new Error('MALFORMED_CONFLICT');
      throw new RemoteConflictError(
        clientFromProto(response.outcome.value.representation.value),
        bytesToUuid(response.outcome.value.rejectedOperationId),
      );
    }
    if (response.outcome.case !== 'client') throw new Error('MALFORMED_RESPONSE');
    const client = clientFromProto(response.outcome.value);
    this.#clients.set(client.id, client);
    this.emitClients();
    return client;
  }

  async updateClient(input: ClientUpdateInput): Promise<ClientView> {
    const update: {
      updateMask: string[];
      name?: string;
      email?: string;
      status?: string;
      customFields?: Record<string, string>;
    } = {
      updateMask: [],
    };
    if (input.name !== undefined) {
      update.name = input.name;
      update.updateMask.push('name');
    }
    if (input.email !== undefined) {
      update.email = input.email;
      update.updateMask.push('email');
    }
    if (input.status !== undefined) {
      update.status = input.status;
      update.updateMask.push('status');
    }
    if (input.customFields !== undefined) {
      update.customFields = input.customFields;
      update.updateMask.push('custom_fields');
    }
    if (update.updateMask.length === 0) throw new Error('VALIDATION_FAILED');
    await this.connect();
    const command = create(CommandRequestV1Schema, {
      operationId: uuidToBytes(randomUuid()),
      actorProfileId: this.actorProfileId,
      entityType: EntityTypeV1.CLIENT,
      entityId: uuidToBytes(input.id),
      baseRevision: BigInt(input.baseRevision),
      command: { case: 'updateClient', value: create(UpdateClientV1Schema, update) },
    });
    const response = await this.call({ case: 'request', value: command });
    if (response.outcome.case === 'conflict') {
      if (response.outcome.value.representation.case !== 'client')
        throw new Error('MALFORMED_CONFLICT');
      throw new RemoteConflictError(
        clientFromProto(response.outcome.value.representation.value),
        bytesToUuid(response.outcome.value.rejectedOperationId),
      );
    }
    if (response.outcome.case !== 'client') throw new Error('MALFORMED_RESPONSE');
    const client = clientFromProto(response.outcome.value);
    this.#clients.set(client.id, client);
    this.emitClients();
    return client;
  }

  async archiveClient(input: { id: string; baseRevision: number }): Promise<ClientView> {
    await this.connect();
    const command = create(CommandRequestV1Schema, {
      operationId: uuidToBytes(randomUuid()),
      actorProfileId: this.actorProfileId,
      entityType: EntityTypeV1.CLIENT,
      entityId: uuidToBytes(input.id),
      baseRevision: BigInt(input.baseRevision),
      command: { case: 'archiveClient', value: create(ArchiveClientV1Schema) },
    });
    const response = await this.call({ case: 'request', value: command });
    if (response.outcome.case === 'conflict') {
      if (response.outcome.value.representation.case !== 'client')
        throw new Error('MALFORMED_CONFLICT');
      throw new RemoteConflictError(
        clientFromProto(response.outcome.value.representation.value),
        bytesToUuid(response.outcome.value.rejectedOperationId),
      );
    }
    if (response.outcome.case !== 'client') throw new Error('MALFORMED_RESPONSE');
    const client = clientFromProto(response.outcome.value);
    this.#clients.set(client.id, client);
    this.emitClients();
    return client;
  }

  subscribe(listener: (clients: readonly ClientView[]) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  subscribeDocuments(
    clientId: string,
    listener: (documents: readonly DocumentView[]) => void,
  ): () => void {
    const listeners = this.#documentListeners.get(clientId) ?? new Set();
    listeners.add(listener);
    this.#documentListeners.set(clientId, listeners);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) this.#documentListeners.delete(clientId);
    };
  }

  subscribeFile(listener: (transfer: FileTransferV1) => void): () => void {
    this.#fileListeners.add(listener);
    return () => this.#fileListeners.delete(listener);
  }

  subscribePresence(listener: (presence: PresenceV1) => void): () => void {
    this.#presenceListeners.add(listener);
    return () => this.#presenceListeners.delete(listener);
  }

  async sendFileTransfer(transfer: FileTransferV1): Promise<void> {
    if (!this.fileChannel || !this.fileKey || !this.fileNoncePrefix) {
      throw new Error('FILE_CHANNEL_UNAVAILABLE');
    }
    await this.connect();
    if (this.fileChannel.readyState !== 'open') throw new Error('FILE_CHANNEL_NOT_CONNECTED');
    const envelope = create(EnvelopeV1Schema, {
      messageId: uuidToBytes(this.requestId()),
      sentAtMs: BigInt(Date.now()),
      payload: { case: 'fileTransfer', value: transfer },
    });
    const frame = await encodeFrame({
      channel: FRAME_CHANNEL.FILE,
      direction: FRAME_DIRECTION.BROWSER_TO_PHONE,
      sessionId: this.sessionId,
      sequence: this.#fileSequence++,
      plaintext: toBinary(EnvelopeV1Schema, envelope),
      key: this.fileKey,
      noncePrefix: this.fileNoncePrefix,
    });
    this.fileChannel.send(frame);
  }

  async sendPresence(presence: PresenceV1): Promise<void> {
    if (!this.presenceChannel || !this.presenceKey || !this.presenceNoncePrefix) {
      throw new Error('PRESENCE_CHANNEL_UNAVAILABLE');
    }
    await this.connect();
    if (this.presenceChannel.readyState !== 'open')
      throw new Error('PRESENCE_CHANNEL_NOT_CONNECTED');
    const envelope = create(EnvelopeV1Schema, {
      messageId: uuidToBytes(this.requestId()),
      sentAtMs: BigInt(Date.now()),
      payload: { case: 'presence', value: presence },
    });
    const frame = await encodeFrame({
      channel: FRAME_CHANNEL.PRESENCE,
      direction: FRAME_DIRECTION.BROWSER_TO_PHONE,
      sessionId: this.sessionId,
      sequence: this.#presenceSequence++,
      plaintext: toBinary(EnvelopeV1Schema, envelope),
      key: this.presenceKey,
      noncePrefix: this.presenceNoncePrefix,
    });
    this.presenceChannel.send(frame);
  }

  /** Announces that this browser is viewing one authorized entity for the 10-second lease. */
  async announceViewing(
    entityType: EntityTypeV1,
    entityId: string,
    actorDisplayLabel = 'This browser',
  ): Promise<void> {
    await this.sendPresence(
      create(PresenceV1Schema, {
        sessionId: new Uint8Array(this.sessionId),
        actorDisplayLabel,
        entityType,
        entityId: uuidToBytes(entityId),
        state: PresenceStateV1.VIEWING,
        clientCounter: this.#presenceCounter++,
      }),
    );
  }

  close(): void {
    this.failClosed(new Error('SESSION_CLOSED'));
    this.channel.close();
    this.fileChannel?.close();
    this.presenceChannel?.close();
  }

  private async call(payload: EnvelopeV1['payload']): Promise<ResponseV1> {
    await this.connect();
    const id = this.requestId();
    const messageId = uuidToBytes(id);
    const pending = new Promise<unknown>((resolve, reject) =>
      this.#pending.set(id, { resolve, reject }),
    );
    try {
      const envelope = create(EnvelopeV1Schema, {
        messageId,
        sentAtMs: BigInt(Date.now()),
        payload,
      });
      const frame = await encodeFrame({
        channel: FRAME_CHANNEL.CONTROL,
        direction: FRAME_DIRECTION.BROWSER_TO_PHONE,
        sessionId: this.sessionId,
        sequence: this.#sequence++,
        plaintext: toBinary(EnvelopeV1Schema, envelope),
        key: this.key,
        noncePrefix: this.noncePrefix,
      });
      this.channel.send(frame);
    } catch (error) {
      this.#pending.delete(id);
      throw error;
    }
    return (await pending) as ResponseV1;
  }

  private async handleMessage(event: unknown): Promise<void> {
    const raw = (event as { data?: unknown })?.data ?? event;
    const frame = await toFrameBytes(raw);
    if (!frame) return this.failClosed(new Error('MALFORMED_FRAME'));
    try {
      const decoded = await decodeFrame(frame, this.responseKey, this.responseNoncePrefix);
      if (
        decoded.direction !== FRAME_DIRECTION.PHONE_TO_BROWSER ||
        !equalBytes(decoded.sessionId, this.sessionId)
      )
        throw new Error('FRAME_DIRECTION');
      this.#incoming.accept(decoded.sequence);
      const envelope = fromBinary(EnvelopeV1Schema, decoded.plaintext);
      validateEnvelope(envelope);
      if (envelope.messageId.byteLength !== 16) throw new Error('MALFORMED_RESPONSE');
      if (envelope.payload.case === 'fileTransfer') return;
      if (envelope.payload.case === 'committedEvent') {
        const committed = envelope.payload.value;
        if (committed.syncCursor.byteLength > 0) {
          this.#syncCursor = new Uint8Array(committed.syncCursor);
        }
        if (committed.representation.case === 'client' && committed.representation.value) {
          this.#clients.set(
            bytesToUuid(committed.representation.value.clientId),
            clientFromProto(committed.representation.value),
          );
          this.emitClients();
        } else if (committed.representation.case === 'document' && committed.representation.value) {
          this.upsertDocument(documentFromProto(committed.representation.value));
        }
        return;
      }
      const id = bytesToUuid(envelope.messageId);
      const pending = this.#pending.get(id);
      if (!pending) throw new Error('UNKNOWN_REQUEST');
      this.#pending.delete(id);
      if (envelope.payload.case === 'sessionControl') {
        if (envelope.payload.value.message.case !== 'ready') throw new Error('SESSION_NOT_READY');
        pending.resolve(envelope.payload.value.message.value);
        return;
      }
      if (envelope.payload.case !== 'response') throw new Error('MALFORMED_RESPONSE');
      const response = envelope.payload.value;
      if (
        response.requestId.byteLength !== 16 ||
        !equalBytes(response.requestId, envelope.messageId)
      )
        throw new Error('RESPONSE_CORRELATION');
      if (response.outcome.case === 'error') {
        pending.reject(new Error(errorName(response.outcome.value.code)));
      } else {
        pending.resolve(response);
      }
    } catch (error) {
      this.failClosed(error instanceof Error ? error : new Error('MALFORMED_FRAME'));
    }
  }

  private async handleFileMessage(event: unknown): Promise<void> {
    if (!this.fileResponseKey || !this.fileResponseNoncePrefix)
      return this.failClosed(new Error('FILE_CHANNEL_MATERIAL_UNAVAILABLE'));
    const raw = (event as { data?: unknown })?.data ?? event;
    const frame = await toFrameBytes(raw);
    if (!frame) return this.failClosed(new Error('MALFORMED_FILE_FRAME'));
    try {
      const decoded = await decodeFrame(frame, this.fileResponseKey, this.fileResponseNoncePrefix);
      if (
        decoded.channel !== FRAME_CHANNEL.FILE ||
        decoded.direction !== FRAME_DIRECTION.PHONE_TO_BROWSER ||
        !equalBytes(decoded.sessionId, this.sessionId)
      ) {
        throw new Error('FILE_FRAME_DIRECTION');
      }
      this.#incomingFile.accept(decoded.sequence);
      const envelope = fromBinary(EnvelopeV1Schema, decoded.plaintext);
      validateEnvelope(envelope);
      if (envelope.payload.case !== 'fileTransfer') throw new Error('MALFORMED_FILE_RESPONSE');
      const transfer = envelope.payload.value;
      if (!transfer) throw new Error('MALFORMED_FILE_RESPONSE');
      this.#fileListeners.forEach((listener) => listener(transfer));
    } catch (error) {
      this.failClosed(error instanceof Error ? error : new Error('MALFORMED_FILE_FRAME'));
    }
  }

  private async handlePresenceMessage(event: unknown): Promise<void> {
    if (!this.presenceResponseKey || !this.presenceResponseNoncePrefix)
      return this.failClosed(new Error('PRESENCE_CHANNEL_MATERIAL_UNAVAILABLE'));
    const raw = (event as { data?: unknown })?.data ?? event;
    const frame = await toFrameBytes(raw);
    if (!frame) return this.failClosed(new Error('MALFORMED_PRESENCE_FRAME'));
    try {
      const decoded = await decodeFrame(
        frame,
        this.presenceResponseKey,
        this.presenceResponseNoncePrefix,
      );
      if (
        decoded.channel !== FRAME_CHANNEL.PRESENCE ||
        decoded.direction !== FRAME_DIRECTION.PHONE_TO_BROWSER ||
        !equalBytes(decoded.sessionId, this.sessionId)
      ) {
        throw new Error('PRESENCE_FRAME_DIRECTION');
      }
      if (this.#incomingPresence.accept(decoded.sequence) !== 'accepted') return;
      const envelope = fromBinary(EnvelopeV1Schema, decoded.plaintext);
      validateEnvelope(envelope);
      if (envelope.payload.case !== 'presence') throw new Error('MALFORMED_PRESENCE');
      const presence = envelope.payload.value;
      if (!presence) throw new Error('MALFORMED_PRESENCE');
      this.#presenceListeners.forEach((listener) => listener(presence));
    } catch (error) {
      this.failClosed(error instanceof Error ? error : new Error('MALFORMED_PRESENCE_FRAME'));
    }
  }

  private async performHandshake(): Promise<SessionReadyV1> {
    if (!this.handshake) throw new Error('HANDSHAKE_UNAVAILABLE');
    if (!this.#handshakePromise) {
      this.#handshakePromise = (async () => {
        const id = this.requestId();
        const messageId = uuidToBytes(id);
        const pending = new Promise<unknown>((resolve, reject) =>
          this.#pending.set(id, { resolve, reject }),
        );
        const confirm = create(SessionConfirmV1Schema, {
          browserPublicKeyFingerprint: this.handshake!.browserPublicKeyFingerprint,
          phonePublicKeyFingerprint: this.handshake!.phonePublicKeyFingerprint,
          offerHash: this.handshake!.offerHash,
          protocolMajor: this.handshake!.protocolMajor ?? 1,
        });
        const envelope = create(EnvelopeV1Schema, {
          messageId,
          sentAtMs: BigInt(Date.now()),
          payload: {
            case: 'sessionControl',
            value: create(SessionControlV1Schema, {
              message: { case: 'confirm', value: confirm },
            }),
          },
        });
        try {
          const frame = await encodeFrame({
            channel: FRAME_CHANNEL.CONTROL,
            direction: FRAME_DIRECTION.BROWSER_TO_PHONE,
            sessionId: this.sessionId,
            sequence: this.#sequence++,
            plaintext: toBinary(EnvelopeV1Schema, envelope),
            key: this.key,
            noncePrefix: this.noncePrefix,
          });
          this.channel.send(frame);
          const ready = (await pending) as SessionReadyV1;
          if (
            ready.sessionId.byteLength !== 16 ||
            !equalBytes(ready.sessionId, this.sessionId) ||
            ready.actorProfileId.byteLength !== 16 ||
            ready.syncStart !== SyncStartV1.REQUIRED
          )
            throw new Error('MALFORMED_SESSION_READY');
          this.actorProfileId = new Uint8Array(ready.actorProfileId);
          this.#sessionReady = true;
          return ready;
        } catch (error) {
          this.#pending.delete(id);
          throw error;
        }
      })().finally(() => {
        this.#handshakePromise = undefined;
      });
    }
    return this.#handshakePromise;
  }

  private failClosed(error: Error): void {
    this.#status = 'DISCONNECTED';
    this.#sessionReady = false;
    this.#pending.forEach(({ reject }) => reject(error));
    this.#pending.clear();
    this.#clients.clear();
    this.#documents.clear();
    this.#documentListeners.clear();
    // Publish the terminal empty snapshot before dropping listeners so the shell can render
    // DISCONNECTED instead of leaving the last CONNECTED view on screen after a channel fault.
    this.#listeners.forEach((listener) => listener([]));
    this.#listeners.clear();
  }

  private emitClients(): void {
    const request = this.#clientListRequest;
    const query = request.query?.trim().toLocaleLowerCase() ?? '';
    const snapshot = [...this.#clients.values()]
      .filter((client) => request.includeArchived || client.status !== 'INACTIVE')
      .filter((client) => !request.status || client.status === request.status)
      .filter(
        (client) => !query || `${client.name} ${client.email}`.toLocaleLowerCase().includes(query),
      )
      .sort(
        (left, right) => right.updatedAtMs - left.updatedAtMs || left.id.localeCompare(right.id),
      );
    this.#listeners.forEach((listener) => listener(snapshot));
  }

  private upsertDocument(document: DocumentView): void {
    const documents = this.#documents.get(document.clientId) ?? new Map<string, DocumentView>();
    documents.set(document.id, document);
    this.#documents.set(document.clientId, documents);
    this.emitDocuments(document.clientId);
  }

  private emitDocuments(clientId: string): void {
    const snapshot = [...(this.#documents.get(clientId)?.values() ?? [])].sort(
      (left, right) => right.updatedAtMs - left.updatedAtMs || left.id.localeCompare(right.id),
    );
    this.#documentListeners.get(clientId)?.forEach((listener) => listener(snapshot));
  }
}

async function toFrameBytes(raw: unknown): Promise<Uint8Array | undefined> {
  if (raw instanceof Uint8Array) return raw;
  if (raw instanceof ArrayBuffer) return new Uint8Array(raw);
  if (typeof Blob !== 'undefined' && raw instanceof Blob)
    return new Uint8Array(await raw.arrayBuffer());
  return undefined;
}

function clientFromProto(client: ClientSnapshotV1): ClientView {
  return {
    id: bytesToUuid(client.clientId),
    ownerActorId: bytesToUuid(client.ownerActorId),
    name: client.name,
    email: client.email,
    status: client.archived ? 'INACTIVE' : client.status,
    revision: Number(client.revision),
    updatedAtMs: Number(client.updatedAtMs),
    ...(client.customFields && Object.keys(client.customFields).length > 0
      ? { customFields: { ...client.customFields } }
      : {}),
  };
}

function documentFromProto(document: {
  documentId: Uint8Array;
  clientId: Uint8Array;
  displayName: string;
  category: string;
  status: string;
  revision: bigint;
  activeFileVersionId: Uint8Array;
  notes: string;
  updatedAtMs: bigint;
  archived: boolean;
}): DocumentView {
  return {
    id: bytesToUuid(document.documentId),
    clientId: bytesToUuid(document.clientId),
    displayName: document.displayName,
    category: document.category,
    status: document.archived ? 'INACTIVE' : document.status,
    revision: Number(document.revision),
    activeFileVersionId: bytesToUuid(document.activeFileVersionId),
    notes: document.notes,
    updatedAtMs: Number(document.updatedAtMs),
  };
}

function errorName(code: ErrorCodeV1): string {
  return ErrorCodeV1[code] ?? 'REMOTE_COMMAND_FAILED';
}

function uuidToBytes(value: string): Uint8Array {
  const normalized = value.trim().toLowerCase();
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(normalized)) {
    throw new Error('INVALID_UUID');
  }
  return Uint8Array.from(normalized.replaceAll('-', '').match(/../g)!, (part) =>
    parseInt(part, 16),
  );
}

function bytesToUuid(value: Uint8Array): string {
  if (value.byteLength !== 16) throw new Error('INVALID_UUID_BYTES');
  const hex = [...value].map((part) => part.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) return false;
  return left.every((value, index) => value === right[index]);
}
